import { type FirebaseOptions, getApp, initializeApp } from 'firebase/app'
import {
	getAuth,
	getIdToken,
	onAuthStateChanged,
	type User,
} from 'firebase/auth'

const firebaseConfig = {
	apiKey:
		process.env.EXPO_PUBLIC_FIREBASE_API_KEY ||
		process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
	appId:
		process.env.EXPO_PUBLIC_FIREBASE_APP_ID ||
		process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
	authDomain:
		process.env.EXPO_PUBLIC_FIREBASE_AUTH_DOMAIN ||
		process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
	databaseURL: `https://${process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID}-default-rtdb.europe-west1.firebasedatabase.app`,
	measurementId:
		process.env.EXPO_PUBLIC_FIREBASE_MEASUREMENT_ID ||
		process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
	messagingSenderId:
		process.env.EXPO_PUBLIC_FIREBASE_MESSAGING_SENDER_ID ||
		process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
	projectId:
		process.env.EXPO_PUBLIC_FIREBASE_PROJECT_ID ||
		process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
	storageBucket:
		process.env.EXPO_PUBLIC_FIREBASE_STORAGE_BUCKET ||
		process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
}

export const getFirebaseApp = (config: FirebaseOptions = firebaseConfig) => {
	try {
		return getApp()
	} catch {
		return initializeApp(config)
	}
}

getFirebaseApp(firebaseConfig)

const AUTH_STATE_TIMEOUT_MS = 5_000

const waitForAuthUser = (): Promise<User | null> => {
	const auth = getAuth()

	if (auth.currentUser) {
		return Promise.resolve(auth.currentUser)
	}

	return new Promise((resolve) => {
		let settled = false

		const finish = (user: User | null) => {
			if (settled) return
			settled = true
			resolve(user)
		}

		try {
			const unsub = onAuthStateChanged(auth, (user) => {
				unsub()
				finish(user)
			})

			setTimeout(() => {
				if (settled) return
				unsub()
				finish(auth.currentUser)
			}, AUTH_STATE_TIMEOUT_MS)
		} catch {
			finish(null)
		}
	})
}

export const getUserToken = async (): Promise<string | undefined> => {
	try {
		const user = await waitForAuthUser()
		if (!user) return undefined

		return await Promise.race([
			getIdToken(user),
			new Promise<undefined>((resolve) => {
				setTimeout(() => resolve(undefined), AUTH_STATE_TIMEOUT_MS)
			}),
		])
	} catch {
		return undefined
	}
}

export const getUser = async (): Promise<User | undefined> => {
	try {
		const user = await waitForAuthUser()
		return user ?? undefined
	} catch {
		return undefined
	}
}
